import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Search, Package, Printer } from "lucide-react";
import { businessApi, API } from "../../lib/api";

const STATUS_LABEL = {
  created: { label: "Oprettet", color: "bg-slate-100 text-slate-800" },
  ready_for_pickup: { label: "Klar til afhentning", color: "bg-blue-100 text-blue-800" },
  picked_up: { label: "Afhentet", color: "bg-indigo-100 text-indigo-800" },
  at_hub: { label: "På HUB", color: "bg-purple-100 text-purple-800" },
  sorted: { label: "Sorteret", color: "bg-purple-100 text-purple-800" },
  out_for_delivery: { label: "Ud til levering", color: "bg-amber-100 text-amber-800" },
  delivered: { label: "Leveret", color: "bg-emerald-100 text-emerald-800" },
  failed_delivery: { label: "Mislykket levering", color: "bg-red-100 text-red-800" },
  redelivery_requested: { label: "Genlevering anmodet", color: "bg-orange-100 text-orange-800" },
  returned: { label: "Returneret", color: "bg-gray-200 text-gray-800" },
  cancelled: { label: "Annulleret", color: "bg-gray-100 text-gray-600" },
};

export default function BusinessShipmentsListPage() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [productFilter, setProductFilter] = React.useState("");
  const [selected, setSelected] = React.useState(new Set());
  const [bulkBusy, setBulkBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter) params.set("status", statusFilter);
      if (productFilter) params.set("product", productFilter);
      const { data } = await businessApi().get(`/business/shipments?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke hente forsendelser"); }
    setLoading(false);
  }, [q, statusFilter, productFilter]);

  React.useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-5" data-testid="business-shipments-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-brand-red font-semibold">Forsendelser</div>
          <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">Dine forsendelser</h1>
        </div>
        {selected.size > 0 && (
          <BulkLabelButton ids={Array.from(selected)} busy={bulkBusy} setBusy={setBulkBusy}/>
        )}
        <Link to="/business/create" className="btn-primary ml-auto" data-testid="biz-shipments-new">
          <Plus size={16}/> Ny forsendelse
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white border border-brand p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="label-flat">Søg (Forsendelse-ID, ref, modtager, postnr)</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-text2"/>
            <input className="input-flat pl-7" value={q} onChange={(e) => setQ(e.target.value)} placeholder="EMA-2026-000001..." data-testid="biz-ship-search"/>
          </div>
        </div>
        <div>
          <label className="label-flat">Status</label>
          <select className="input-flat" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="biz-ship-status-filter">
            <option value="">Alle</option>
            {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s].label}</option>)}
          </select>
        </div>
        <div>
          <label className="label-flat">Produkt</label>
          <select className="input-flat" value={productFilter} onChange={(e) => setProductFilter(e.target.value)} data-testid="biz-ship-product-filter">
            <option value="">Alle</option>
            <option value="same_day_b2b">Same Day Business</option>
            <option value="next_day_b2b">Next Day Business</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-brand-text2 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Indlæser...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-brand p-8 text-center text-brand-text2" data-testid="biz-shipments-empty">
          <Package size={32} className="mx-auto mb-2 text-brand-text2/60"/>
          Ingen forsendelser endnu. Klik &quot;Ny forsendelse&quot; for at oprette den første.
        </div>
      ) : (
        <div className="bg-white border border-brand overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={(e) => setSelected(e.target.checked ? new Set(items.map((s) => s.id)) : new Set())} data-testid="biz-ship-select-all"/>
                </th>
                <th className="px-3 py-2 text-left">Forsendelse</th>
                <th className="px-3 py-2 text-left">Ref</th>
                <th className="px-3 py-2 text-left">Modtager</th>
                <th className="px-3 py-2 text-left">Adresse</th>
                <th className="px-3 py-2 text-left">Produkt</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Pris</th>
                <th className="px-3 py-2 text-left">Oprettet</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const lbl = STATUS_LABEL[s.status] || { label: s.status, color: "bg-gray-100 text-gray-700" };
                return (
                  <tr key={s.id} className="border-t border-gray-100 hover:bg-brand-bg2/50" data-testid={`biz-ship-row-${s.shipment_number}`}>
                    <td className="px-2 py-2"><input type="checkbox" checked={selected.has(s.id)} onChange={(e) => {
                      setSelected((p) => { const n = new Set(p); if (e.target.checked) n.add(s.id); else n.delete(s.id); return n; });
                    }} data-testid={`biz-ship-select-${s.id}`}/></td>
                    <td className="px-3 py-2"><Link to={`/business/shipments/${s.id}`} className="font-mono text-brand-red underline">{s.shipment_number}</Link></td>
                    <td className="px-3 py-2 text-xs text-brand-text2">{s.customer_reference || "—"}</td>
                    <td className="px-3 py-2">{s.recipient_name}</td>
                    <td className="px-3 py-2 text-xs">{s.delivery_postal_code} {s.delivery_city}</td>
                    <td className="px-3 py-2 text-xs">{s.product === "same_day_b2b" ? "Same Day" : "Next Day"}</td>
                    <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 ${lbl.color}`}>{lbl.label}</span></td>
                    <td className="px-3 py-2 text-right text-xs">{s.price?.total_dkk?.toFixed(2)} kr</td>
                    <td className="px-3 py-2 text-xs text-brand-text2">{new Date(s.created_at_iso).toLocaleString("da-DK")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { STATUS_LABEL };

function BulkLabelButton({ ids, busy, setBusy }) {
  const [fmt, setFmt] = React.useState("thermal");
  const download = async () => {
    setBusy(true);
    try {
      const tk = localStorage.getItem("ema_business_token");
      const res = await fetch(`${API}/business/shipments/labels.pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tk}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ids, fmt }),
      });
      if (!res.ok) throw new Error("Bulk label fejl");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`${ids.length} labels genereret`);
    } catch { toast.error("Kunne ikke generere labels"); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-2 bg-brand-bg2 px-3 py-1.5 border border-brand">
      <span className="text-sm text-brand-dark">{ids.length} valgt</span>
      <select value={fmt} onChange={(e) => setFmt(e.target.value)} className="text-xs px-2 py-1 border border-brand bg-white" data-testid="biz-bulk-label-fmt">
        <option value="thermal">10×15 thermal</option>
        <option value="a6">A6 (4-up)</option>
      </select>
      <button onClick={download} disabled={busy} className="btn-primary text-sm" data-testid="biz-bulk-label-btn">
        {busy ? <Loader2 className="animate-spin" size={14}/> : <Printer size={14}/>} Download labels
      </button>
    </div>
  );
}

